import { useState, type FormEvent } from 'react';
import { supabase, SOURCE_BRAND } from './supabase';

type FormState = 'idle' | 'submitting' | 'success' | 'error';

export function App() {
  const [email, setEmail] = useState('');
  // Honeypot — bots fill visible-to-them-but-hidden-to-humans fields.
  // Any submission with a non-empty honeypot is silently dropped.
  const [honeypot, setHoneypot] = useState('');
  const [state, setState] = useState<FormState>('idle');
  const [error, setError] = useState<string | null>(null);

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setError(null);

    // Drop bot submissions silently (bots won't notice).
    if (honeypot.length > 0) {
      setState('success');
      return;
    }

    if (!email || !email.includes('@')) {
      setError('Drop a valid email.');
      return;
    }

    if (!supabase) {
      setError('Waitlist is warming up. Try again in a moment.');
      return;
    }

    setState('submitting');
    const { error: insertError } = await supabase.from('waitlist_submissions').insert({
      email: email.trim().toLowerCase(),
      source_brand: SOURCE_BRAND,
      user_agent: navigator.userAgent.slice(0, 500),
      referrer: document.referrer.slice(0, 500) || null,
    });

    if (insertError) {
      // Unique violation = already on the list; treat as success.
      if (insertError.code === '23505') {
        setState('success');
      } else {
        setState('error');
        setError('Something broke. Try again?');
      }
      return;
    }

    setState('success');
    setEmail('');
  }

  return (
    <main className="mx-auto flex min-h-screen max-w-2xl flex-col justify-between px-6 py-10 md:px-8">
      <Brand />

      <section className="flex flex-col gap-6">
        <Badge>Launching 2026</Badge>
        <h1 className="text-5xl leading-[1.05] font-bold tracking-tight text-white md:text-7xl">
          Storage unit auctions,
          <br />
          <span className="text-[color:var(--brand-amber)]">reimagined.</span>
        </h1>
        <p className="max-w-xl text-lg leading-relaxed text-[color:var(--brand-text-muted)] md:text-xl">
          Modern bidding for the Storage Wars generation. Every facility, every state, every locker
          — one marketplace.
        </p>

        {state === 'success' ? (
          <SuccessMessage />
        ) : (
          <form
            onSubmit={(event) => void handleSubmit(event)}
            className="mt-4 flex flex-col gap-3 sm:flex-row sm:gap-2"
          >
            {/* Honeypot — hidden from humans with CSS, visible to bots */}
            <label className="sr-only" aria-hidden>
              Leave blank
              <input
                type="text"
                tabIndex={-1}
                autoComplete="off"
                value={honeypot}
                onChange={(event) => setHoneypot(event.target.value)}
              />
            </label>

            <label className="flex-1">
              <span className="sr-only">Email address</span>
              <input
                type="email"
                required
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                placeholder="you@hunt.email"
                className="w-full rounded-md border border-[color:var(--brand-border)] bg-[color:var(--brand-bg-raised)] px-4 py-3 text-base text-white placeholder:text-neutral-500 focus:border-[color:var(--brand-amber)] focus:outline-none"
                disabled={state === 'submitting'}
              />
            </label>
            <button
              type="submit"
              disabled={state === 'submitting'}
              className="rounded-md bg-[color:var(--brand-amber)] px-6 py-3 font-semibold text-black transition-colors hover:bg-[color:var(--brand-amber-bright)] disabled:opacity-60"
            >
              {state === 'submitting' ? 'Sending…' : 'Notify me'}
            </button>
          </form>
        )}

        {error && <p className="text-sm text-red-400">{error}</p>}
      </section>

      <Footer />
    </main>
  );
}

function Brand() {
  return (
    <header className="flex items-center gap-3">
      <BrandMark />
      <span className="text-2xl font-bold tracking-tight text-white">LockerBids</span>
    </header>
  );
}

function BrandMark() {
  return (
    <svg className="h-9 w-9" viewBox="0 0 64 64" aria-hidden>
      <rect width="64" height="64" rx="12" fill="var(--brand-bg-raised)" />
      <path
        d="M32 14 L46 22 L46 42 L32 50 L18 42 L18 22 Z"
        fill="none"
        stroke="var(--brand-amber)"
        strokeWidth="3"
        strokeLinejoin="round"
      />
      <circle cx="32" cy="32" r="4" fill="var(--brand-amber)" />
      <line
        x1="32"
        y1="32"
        x2="32"
        y2="42"
        stroke="var(--brand-amber)"
        strokeWidth="3"
        strokeLinecap="round"
      />
    </svg>
  );
}

function Badge({ children }: { children: React.ReactNode }) {
  return (
    <span
      className="inline-flex w-fit items-center gap-2 rounded-full border px-3 py-1 text-xs font-semibold tracking-widest uppercase"
      style={{ borderColor: 'var(--brand-amber)', color: 'var(--brand-amber)' }}
    >
      <span className="h-1.5 w-1.5 rounded-full bg-[color:var(--brand-amber)]" />
      {children}
    </span>
  );
}

function SuccessMessage() {
  return (
    <div className="mt-4 rounded-md border border-[color:var(--brand-amber)] bg-[color:var(--brand-bg-raised)] p-4">
      <p className="font-semibold text-white">You're on the list.</p>
      <p className="mt-1 text-sm text-[color:var(--brand-text-muted)]">
        We'll email when LockerBids goes live. No spam, no daily blasts — just the drop.
      </p>
    </div>
  );
}

function Footer() {
  return (
    <footer className="flex flex-col gap-2 text-sm text-[color:var(--brand-text-muted)]">
      <p>
        A consumer brand from{' '}
        <a
          href="https://auctisync.com"
          className="font-medium text-white underline-offset-4 hover:underline"
        >
          AuctiSync
        </a>
        .
      </p>
      <p className="text-xs">© 2026 AuctiSync. All rights reserved.</p>
    </footer>
  );
}
